<template>
  <div class="lead-detail">
    <van-nav-bar title="客资详情" left-arrow @click-left="goBack" />
    <div v-if="loading" class="state"><van-loading size="24px">加载中...</van-loading></div>
    <div v-else-if="loadError" class="state">
      <p>客资详情加载失败</p>
      <van-button type="info" size="small" @click="loadPage">重新加载</van-button>
    </div>
    <template v-else>
      <header class="identity">
        <div class="initial">
          <img v-if="lead.avatar && !avatarFailed" :src="lead.avatar" alt="" @error="avatarFailed = true" />
          <span v-else>{{ String(lead.name || '客').slice(0, 1) }}</span>
        </div>
        <div class="identity-body">
          <h1>{{ leadDisplayName(lead.name) }}</h1>
          <div>{{ displayValue(lead.mobile) }}</div>
          <div class="states">
            <span class="stage">{{ label('businessStages', lead.businessStage) }}</span>
            <span :class="isBound ? 'linked' : 'unlinked'">{{ bindingStatusText }}</span>
          </div>
        </div>
      </header>

      <section class="section wecom-section">
        <h2>企微联动</h2>
        <van-cell title="关联状态" :value="bindingStatusText" />
        <van-cell title="同步状态" :value="syncStatusText" />
        <van-cell title="二维码有效期" :value="qrCodeValidityText" />
        <van-cell title="企业客户标签" :value="wecomTagText" />
        <div v-if="addFriendResultStatus" class="wecom-result">
          <strong>{{ addFriendResultTitle }}</strong>
          <p>{{ addFriendResultMessage }}</p>
          <van-button v-if="pendingAddFriendData" size="small" type="info" plain :loading="addingFriend" :disabled="wecomActionBusy" @click="continueAddFriend">继续去企微添加</van-button>
        </div>
        <div v-if="addFriendError" class="wecom-error">{{ addFriendError }}</div>
        <div v-if="wecomActionError || lead.syncError" class="wecom-error">{{ wecomActionError || lead.syncError }}</div>
        <div v-if="qrCodeUrl && !isBound" class="qr-wrap">
          <img
            class="qr-preview-image"
            :src="qrCodeUrl"
            alt="客资专属企微二维码"
            role="button"
            tabindex="0"
            @click="previewQrCode"
            @keyup.enter.space="previewQrCode"
          />
          <p>请向客户展示或转发此专属二维码，由客户扫码添加负责人企微；添加成功后系统关联客资并同步标签。</p>
        </div>
        <div class="wecom-buttons">
          <van-button v-if="hasBoundContact" type="info" plain icon="contact" @click="openCustomer">查看客户</van-button>
          <van-button v-if="!isBound" type="info" plain :loading="addingFriend" :disabled="wecomActionBusy" @click="addFriend">复制并去企微添加</van-button>
          <van-button v-if="!isBound" type="info" plain :loading="generatingContactWay" :disabled="wecomActionBusy" @click="generateContactWay">生成客资专属二维码</van-button>
          <van-button v-if="canRefreshWecom" type="info" plain :loading="retryingSync" :disabled="wecomActionBusy" @click="retryWecomSync">{{ wecomRefreshLabel }}</van-button>
        </div>
        <p v-if="!isBound" class="sync-explain">手机号或微信号复制后，仍需在企业微信中搜索并确认发送好友申请；也可让客户扫描专属二维码添加。</p>
        <p v-if="lead.bindingStatus === 'awaiting_owner'" class="sync-explain">已关联企业客户，负责人关系待同步确认。若已在企业微信添加该客户，请点击“刷新企微关联状态”。</p>
        <p class="sync-explain">标签同步到企微；跟进记录同步到 MoChat 企微客户跟进视图。</p>
      </section>

      <section class="section">
        <h2>客资资料</h2>
        <van-cell title="负责人" :value="displayValue(lead.ownerEmployeeName)" />
        <van-cell title="来源" :value="label('sources', lead.source)" />
        <van-cell title="下次跟进" :value="displayValue(lead.nextFollowAt)" />
        <van-cell title="创建时间" :value="displayValue(lead.createdAt)" />
      </section>

      <section class="section">
        <h2>学习需求</h2>
        <van-cell title="学员姓名" :value="displayValue(lead.studentName)" />
        <van-cell title="与学员关系" :value="label('relationships', lead.relationship)" />
        <van-cell title="年级" :value="label('grades', lead.grade)" />
        <van-cell title="意向课程" :value="labels('courses', lead.courseCodes, lead.course)" />
        <van-cell title="意向门店" :value="labels('campuses', lead.campusCodes, lead.campus)" />
      </section>

      <section class="section">
        <h2>其他信息</h2>
        <van-cell title="微信号" :value="displayValue(lead.wechatId)" />
        <van-cell title="邮箱" :value="displayValue(lead.email)" />
        <van-cell title="座机" :value="displayValue(lead.telephone)" />
        <van-cell title="性别" :value="label('genders', lead.gender)" />
        <van-cell title="生日" :value="displayValue(lead.birthday)" />
        <van-cell title="地区" :value="displayValue(lead.region)" />
        <div class="remark"><span>备注</span><p>{{ displayValue(lead.remark) }}</p></div>
      </section>

      <section class="section activity-section">
        <h2>跟进记录</h2>
        <p class="activity-sync-note">关联后同步到 MoChat 企微客户跟进视图</p>
        <div v-if="activityError && !activities.length" class="activity-error">跟进记录加载失败 <button type="button" @click="loadActivities(true)">重试</button></div>
        <div v-else-if="!activities.length && !activityLoading" class="activity-empty">暂无跟进记录</div>
        <div v-else class="activity-timeline">
          <div v-for="group in activityGroups" :key="group.date" class="activity-day">
            <div class="activity-day-heading"><span>{{ group.week }}</span><span>{{ group.day }}</span></div>
            <Steps direction="vertical" active-icon="clock" inactive-icon="clock">
              <Step v-for="item in group.items" :key="item.activityId || item.id">
                <div class="activity-entry">
                  <time class="activity-time">{{ activityTime(item.createdAt) }}</time>
                  <span class="activity-icon"><img src="../../assets/tagsImg.png" alt="" /></span>
                  <strong class="activity-title">{{ label('activityTypes', item.type) }}</strong>
                  <p>{{ displayValue(item.content) }}</p>
                  <div v-if="item.operatorName || item.nextFollowAt" class="activity-foot">
                    <span v-if="item.operatorName">{{ item.operatorName }} 设置</span><span v-if="item.operatorName && item.nextFollowAt"> · </span><span v-if="item.nextFollowAt">下次跟进 {{ item.nextFollowAt }}</span>
                  </div>
                </div>
              </Step>
            </Steps>
          </div>
        </div>
        <div v-if="activityError && activities.length" class="activity-error activity-more-error">后续记录加载失败 <button type="button" @click="loadActivities(false)">重试</button></div>
        <van-button v-if="activityHasMore && !activityError" class="more" plain size="small" :loading="activityLoading" @click="loadActivities(false)">加载更多</van-button>
      </section>

      <section v-if="canDestroy" class="section delete-section">
        <van-button block plain type="danger" :loading="deleting" :disabled="confirmingDelete || deleting" @click="confirmDelete">删除客资</van-button>
        <p v-if="deleteError" class="delete-error" role="alert">{{ deleteError }}</p>
      </section>

      <div class="action-bar">
        <van-button plain type="info" @click="openEdit">编辑</van-button>
        <van-button type="info" @click="openFollow">新增跟进</van-button>
      </div>
    </template>

    <van-popup v-model="followVisible" position="bottom" round :style="{ maxHeight: '88vh', overflowY: 'auto' }">
      <div class="follow-head"><strong>新增跟进</strong><button type="button" aria-label="关闭" @click="followVisible = false"><van-icon name="cross" /></button></div>
      <van-form @submit="submitFollow">
        <van-field :value="label('activityTypes', follow.type)" readonly clickable label="跟进方式" placeholder="请选择" @click="showActivityPicker = true" />
        <van-field v-model.trim="follow.content" type="textarea" rows="4" maxlength="1000" show-word-limit label="跟进内容" placeholder="记录沟通结果和下一步计划" :rules="[{ required: true, message: '请输入跟进内容' }]" />
        <div class="datetime-field"><label for="next-follow">下次跟进</label><input id="next-follow" v-model="follow.nextFollowAt" type="datetime-local" /></div>
        <div class="follow-submit"><van-button block type="info" native-type="submit" :loading="submittingFollow">保存跟进</van-button></div>
      </van-form>
    </van-popup>
    <van-popup v-model="showActivityPicker" position="bottom">
      <van-picker show-toolbar :columns="activityChoices" @cancel="showActivityPicker = false" @confirm="chooseActivity" />
    </van-popup>
  </div>
</template>

<script>
import { ImagePreview, Step, Steps } from 'vant'
import { createLeadActivity, createLeadContactWay, deleteLead, getLeadActivities, getLeadDetail, getLeadOptions, prepareLeadAddFriend, retryLeadWeComSync } from '../../api/crmLead'
import { displayValue, enabledChoices, isLeadBound, leadDisplayName, optionLabel } from './leadView'
import { clearLeadListState } from './listState'
import { navigateToAddCustomer, setClipboardData } from '../../utils/wxCodeAuth'

async function copyPlainText (value) {
  if (!value) return false
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(value)
      return true
    } catch (error) {
      // Fall back to the browser copy command when clipboard permission is denied.
    }
  }
  if (typeof document === 'undefined') return false
  const input = document.createElement('textarea')
  input.value = value
  input.setAttribute('readonly', '')
  input.style.position = 'fixed'
  input.style.opacity = '0'
  document.body.appendChild(input)
  input.select()
  let copied = false
  try {
    copied = document.execCommand('copy')
  } catch (error) {
    copied = false
  }
  document.body.removeChild(input)
  return copied
}

async function copyWithWeCom (value) {
  try {
    await setClipboardData(value)
    return true
  } catch (error) {
    return copyPlainText(value)
  }
}

async function openWeComAddCustomer () {
  await navigateToAddCustomer()
}

export default {
  name: 'LeadDetail',
  components: { Step, Steps },
  data () {
    return {
      leadId: Number(this.$route.query.leadId || 0),
      lead: {}, options: {}, loading: true, loadError: false, avatarFailed: false,
      activities: [], activityPage: 1, activityHasMore: false, activityLoading: false, activityError: false,
      generatingContactWay: false, retryingSync: false, generatedQrCode: '', wecomActionError: '',
      addingFriend: false, addFriendResultStatus: '', addFriendResultMessage: '', addFriendError: '', pendingAddFriendData: null,
      confirmingDelete: false, deleting: false, deleteError: '',
      followVisible: false, showActivityPicker: false, submittingFollow: false,
      follow: { type: '', content: '', nextFollowAt: '' }
    }
  },
  computed: {
    hasBoundContact () { return this.isBound && this.linkedContactId > 0 },
    linkedContactId () { return Number(this.lead.boundContactId || this.lead.convertedContactId || 0) },
    activityChoices () {
      return enabledChoices(this.options, 'activityTypes').map(item => ({ text: item.label, value: item.value }))
    },
    activityGroups () {
      const groups = []
      const groupByDate = {}
      this.activities.forEach(item => {
        const date = this.activityDate(item.createdAt)
        if (!groupByDate[date]) {
          groupByDate[date] = {
            date,
            day: date === '日期未知' ? date : date.slice(5),
            week: this.activityWeek(date),
            items: []
          }
          groups.push(groupByDate[date])
        }
        groupByDate[date].items.push(item)
      })
      return groups
    },
    isBound () { return isLeadBound(this.lead) },
    canDestroy () { return this.lead.canDestroy === true },
    wecomActionBusy () { return this.addingFriend || this.generatingContactWay || this.retryingSync },
    bindingStatusText () {
      return ({ unbound: '未关联企微', pending: this.qrCodeUrl ? '等待客户扫码' : '待确认添加结果', awaiting_owner: '已关联客户，负责人关系待确认', bound: '已关联企微', conflict: '绑定冲突', expired: '二维码已过期' })[this.lead.bindingStatus] || (this.isBound ? '已关联企微' : '未关联企微')
    },
    canRefreshWecom () {
      return this.lead.bindingStatus === 'awaiting_owner' || (this.isBound && this.lead.syncStatus === 'failed')
    },
    wecomRefreshLabel () {
      return this.lead.bindingStatus === 'awaiting_owner' ? '刷新企微关联状态' : '重试同步'
    },
    syncStatusText () {
      return ({ not_bound: '待关联后同步', pending: '待同步', syncing: '同步中', synced: '已同步', failed: '同步失败' })[this.lead.syncStatus] || (this.isBound ? '状态未知' : '待关联后同步')
    },
    qrCodeUrl () { return this.generatedQrCode || this.lead.qrCode || '' },
    qrCodeValidityText () {
      const expiresAt = this.lead.bindingExpiresAt || this.lead.expiresAt
      return expiresAt ? displayValue(expiresAt) : (this.qrCodeUrl ? '长期有效' : '-')
    },
    wecomTagText () {
      const selected = Array.isArray(this.lead.wecomTagIds) ? this.lead.wecomTagIds : []
      const names = []
      const groups = Array.isArray(this.options.wecomTagGroups) ? this.options.wecomTagGroups : []
      groups.forEach(group => (group.tags || []).forEach(tag => {
        const id = tag.id !== undefined ? tag.id : tag.value
        if (selected.some(value => String(value) === String(id))) names.push(tag.name || tag.label)
      }))
      return names.length ? names.join('、') : (selected.length ? selected.map(id => `标签 #${id}`).join('、') : '-')
    },
    addFriendResultTitle () {
      return ({ prepared: '已准备添加信息', copied: '添加信息已复制', opened: '已打开企微添加页', already_linked: '客户已关联', awaiting_owner: '已识别客户', cross_employee: '客户已由其他员工添加', ambiguous: '匹配到多个客户' })[this.addFriendResultStatus] || ''
    }
  },
  created () {
    if (!this.leadId) {
      this.loadError = true
      this.loading = false
      return
    }
    this.loadPage()
  },
  beforeRouteLeave (to, from, next) {
    if (!['/customer', '/crmLead/form'].includes(to.path)) clearLeadListState()
    next()
  },
  methods: {
    displayValue,
    leadDisplayName,
    label (key, value) { return optionLabel(this.options, key, value) },
    labels (key, values, fallback) {
      const selected = Array.isArray(values) && values.length ? values : (fallback ? [fallback] : [])
      return selected.length ? selected.map(value => this.label(key, value)).join('、') : '-'
    },
    activityDate (createdAt) {
      const value = String(createdAt || '')
      return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '日期未知'
    },
    activityTime (createdAt) {
      const value = String(createdAt || '')
      return /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(value) ? value.slice(11, 16) : '--:--'
    },
    activityWeek (date) {
      if (date === '日期未知') return ''
      const parts = date.split('-').map(Number)
      const parsed = new Date(parts[0], parts[1] - 1, parts[2])
      return ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][parsed.getDay()]
    },
    goBack () { this.$router.push({ path: '/customer', query: { tab: 'lead' } }) },
    openEdit () {
      this.$router.push({ path: '/crmLead/form', query: { leadId: this.leadId, mode: 'edit' } })
    },
    async confirmDelete () {
      if (this.confirmingDelete || this.deleting) return
      this.deleteError = ''
      this.confirmingDelete = true
      try {
        await this.$dialog.confirm({
          title: '确认删除这条客资？',
          message: '删除后将从客资中心移除，关联的企业微信客户不受影响。',
          confirmButtonText: '删除客资',
          confirmButtonColor: '#ee0a24'
        })
      } catch (error) {
        return
      } finally {
        this.confirmingDelete = false
      }
      this.deleting = true
      try {
        await deleteLead({ leadId: this.leadId, version: this.lead.version })
        this.lead = {}
        this.activities = []
        this.generatedQrCode = ''
        this.followVisible = false
        this.$toast.success('客资已删除')
        this.$router.replace({ path: '/customer', query: { tab: 'lead' } })
      } catch (error) {
        const responseData = error && error.response && error.response.data
        this.deleteError = (responseData && (responseData.msg || responseData.message)) || '删除客资失败，请稍后重试'
      } finally {
        this.deleting = false
      }
    },
    async loadPage () {
      this.loading = true
      this.loadError = false
      try {
        const [detail, choices] = await Promise.all([getLeadDetail(this.leadId), getLeadOptions()])
        this.lead = detail.data || {}
        this.generatedQrCode = this.lead.qrCode || ''
        this.wecomActionError = ''
        this.addFriendResultStatus = ''
        this.addFriendResultMessage = ''
        this.addFriendError = ''
        this.pendingAddFriendData = null
        this.avatarFailed = false
        this.options = choices.data || {}
        this.loadActivities(true)
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    async loadActivities (reset) {
      if (this.activityLoading) return
      if (reset) {
        this.activityPage = 1
        this.activities = []
      }
      this.activityLoading = true
      this.activityError = false
      try {
        const response = await getLeadActivities({ leadId: this.leadId, page: this.activityPage, perPage: 20 })
        const data = response.data || {}
        const list = Array.isArray(data.list) ? data.list : []
        this.activities.push(...list)
        this.activityHasMore = this.activities.length < Number((data.page || {}).total || 0)
        this.activityPage += 1
      } catch (error) {
        this.activityError = true
      } finally {
        this.activityLoading = false
      }
    },
    applyWecomState (data = {}) {
      const hasBindingExpiresAt = Object.prototype.hasOwnProperty.call(data, 'bindingExpiresAt')
      const hasExpiresAt = Object.prototype.hasOwnProperty.call(data, 'expiresAt')
      const customerMatch = data.customerMatch || {}
      const matchStatus = String(data.status || customerMatch.status || '')
      const matchedContactId = Number(customerMatch.contactId || 0)
      const confirmedOwner = ['already_linked', 'linked'].includes(matchStatus) && customerMatch.currentOwnerHasContact === true && matchedContactId > 0
      const matchedBindingStatus = confirmedOwner ? 'bound' : (['awaiting_owner', 'cross_employee'].includes(matchStatus) ? 'awaiting_owner' : '')
      const matchedSyncStatus = confirmedOwner && typeof customerMatch.syncSucceeded === 'boolean' ? (customerMatch.syncSucceeded ? 'synced' : 'failed') : ''
      const stateData = { ...data }
      // Adding-friend responses include revealed identities only for the current manual action.
      for (const field of ['copyText', 'mobile', 'wechatId', 'mobileMasked', 'wechatIdMasked']) delete stateData[field]
      this.lead = {
        ...this.lead,
        ...stateData,
        bindingStatus: data.bindingStatus || matchedBindingStatus || this.lead.bindingStatus,
        syncStatus: data.syncStatus || matchedSyncStatus || this.lead.syncStatus,
        convertedContactId: data.convertedContactId || matchedContactId || this.lead.convertedContactId,
        bindingExpiresAt: hasBindingExpiresAt
          ? data.bindingExpiresAt
          : (hasExpiresAt ? data.expiresAt : this.lead.bindingExpiresAt)
      }
      if (data.qrCode) this.generatedQrCode = data.qrCode
    },
    openCustomer () {
      if (!this.hasBoundContact) return
      this.$router.push({ path: '/contact/detail', query: { contactId: this.linkedContactId } })
    },
    previewQrCode () {
      if (!this.qrCodeUrl) return
      ImagePreview({
        images: [this.qrCodeUrl],
        startPosition: 0,
        showIndex: false,
        closeable: true,
        closeIconPosition: 'top-right',
        closeOnPopstate: true
      })
    },
    async generateContactWay () {
      if (this.wecomActionBusy) return
      this.generatingContactWay = true
      this.wecomActionError = ''
      try {
        const response = await createLeadContactWay({ leadId: this.leadId })
        const data = response.data || {}
        this.applyWecomState(data)
        if (!data.qrCode) this.$toast(this.isBound ? '客户已关联，无需再生成二维码' : (data.message || '暂未取得二维码，请稍后重试'))
      } catch (error) {
        const responseData = error && error.response && error.response.data
        this.wecomActionError = (responseData && (responseData.msg || responseData.message)) || (error && error.message) || '生成二维码失败，请稍后重试'
      } finally {
        this.generatingContactWay = false
      }
    },
    async retryWecomSync () {
      if (this.wecomActionBusy) return
      this.retryingSync = true
      this.wecomActionError = ''
      try {
        const response = await retryLeadWeComSync({ leadId: this.leadId })
        this.applyWecomState(response.data || {})
        if (this.isBound && this.lead.syncStatus === 'synced') this.$toast.success('已确认企微关联并同步资料')
        else if (this.isBound && this.lead.syncStatus === 'failed') this.$toast.fail('客户已关联，资料同步失败，请重试')
        else this.$toast(this.lead.bindingStatus === 'awaiting_owner' ? '暂未确认负责人关系，请稍后刷新' : '企微关联状态已刷新')
      } catch (error) {
        const responseData = error && error.response && error.response.data
        this.wecomActionError = (responseData && (responseData.msg || responseData.message)) || (error && error.message) || '企微关联状态刷新失败，请稍后重试'
      } finally {
        this.retryingSync = false
      }
    },
    async addFriend () {
      if (this.wecomActionBusy) return
      this.addingFriend = true
      this.addFriendResultStatus = ''
      this.addFriendResultMessage = ''
      this.addFriendError = ''
      this.pendingAddFriendData = null
      try {
        const response = await prepareLeadAddFriend({ leadId: this.leadId })
        const data = response.data || {}
        const matchStatus = String(data.status || (data.customerMatch || {}).status || '')
        if (['already_linked', 'awaiting_owner', 'cross_employee', 'ambiguous'].includes(matchStatus)) {
          this.applyWecomState(data)
          this.addFriendResultStatus = matchStatus
          this.addFriendResultMessage = data.message || (data.customerMatch || {}).message || '该手机号已匹配到企业微信客户，请先查看关联信息。'
          if (['awaiting_owner', 'cross_employee'].includes(matchStatus) && data.copyText) {
            this.pendingAddFriendData = {
              copyText: data.copyText, identifierType: data.identifierType,
              canNavigateToAddCustomer: data.canNavigateToAddCustomer, matchMessage: this.addFriendResultMessage
            }
            this.addFriendResultMessage += '；如需添加当前负责人，请点击“继续去企微添加”，在企微中搜索并确认发送申请。'
          }
          return
        }
        await this.copyAndOpenAddFriend(data)
      } catch (error) {
        const responseData = error && error.response && error.response.data
        this.addFriendError = (responseData && (responseData.msg || responseData.message)) || (error && error.message) || '添加信息准备失败，请稍后重试'
      } finally {
        this.addingFriend = false
      }
    },
    async continueAddFriend () {
      if (this.wecomActionBusy || !this.pendingAddFriendData) return
      const data = this.pendingAddFriendData
      this.pendingAddFriendData = null
      this.addingFriend = true
      try {
        await this.copyAndOpenAddFriend(data)
      } catch (error) {
        this.addFriendError = '无法准备添加信息，请重新点击“复制并去企微添加”'
      } finally {
        this.addingFriend = false
      }
    },
    async copyAndOpenAddFriend (data) {
      const identifierName = data.identifierType === 'wechatId' ? '微信号' : '手机号'
      const canNavigate = data.canNavigateToAddCustomer && data.identifierType === 'mobile'
      const copied = canNavigate ? await copyWithWeCom(data.copyText) : await copyPlainText(data.copyText)
      this.addFriendResultStatus = copied ? 'copied' : 'prepared'
      let message = copied
        ? `${identifierName}已复制，请在企业微信中搜索并确认发送好友申请。`
        : '复制失败，请允许剪贴板访问后重试，或使用专属二维码让客户扫码添加。'
      if (copied && canNavigate) {
        try {
          await openWeComAddCustomer()
          this.addFriendResultStatus = 'opened'
          message = `${identifierName}已复制并打开企业微信添加客户页，请粘贴搜索后确认发送好友申请。`
        } catch (error) {
          message = `${identifierName}已复制，但未能打开企业微信添加页，请在企业微信中手动搜索并确认发送好友申请。`
        }
      }
      this.addFriendResultMessage = data.matchMessage ? `${data.matchMessage}；${message}` : message
    },
    openFollow () {
      const first = this.activityChoices.find(item => item.value === 'follow') || this.activityChoices[0]
      if (!first) {
        this.$toast.fail('暂无可用的跟进方式，请联系管理员')
        return
      }
      this.follow = { type: first.value, content: '', nextFollowAt: '' }
      this.followVisible = true
    },
    chooseActivity (choice) {
      this.follow.type = choice.value
      this.showActivityPicker = false
    },
    async submitFollow () {
      if (this.submittingFollow || !this.follow.type) return
      this.submittingFollow = true
      try {
        const response = await createLeadActivity({
          leadId: this.leadId,
          version: this.lead.version,
          type: this.follow.type,
          content: this.follow.content.trim(),
          nextFollowAt: this.follow.nextFollowAt ? this.follow.nextFollowAt.replace('T', ' ') + ':00' : ''
        })
        this.lead.version = (response.data || {}).version
        this.followVisible = false
        this.$toast.success('跟进已保存')
        this.loadActivities(true)
        getLeadDetail(this.leadId).then(detail => { this.lead = detail.data || {} }).catch(() => {})
      } catch (error) {
        // The shared API interceptor presents the server error.
      } finally {
        this.submittingFollow = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.lead-detail { min-height: 100vh; padding-bottom: 80px; background: #f2f4f6; color: #24313a; }
.lead-detail /deep/ .van-nav-bar { position: sticky; top: 0; z-index: 2; }
.state { padding: 60px 16px; text-align: center; }
.identity { display: flex; gap: 13px; padding: 20px 16px; background: #fff; }
.initial { display: grid; place-items: center; flex: 0 0 54px; width: 54px; height: 54px; border-radius: 4px; background: #dfece9; color: #237462; font-size: 24px; font-weight: 600; }
.initial img { width: 54px; height: 54px; border-radius: 4px; object-fit: cover; }
.identity-body { min-width: 0; color: #6b747b; font-size: 13px; }
.identity-body h1 { overflow: hidden; margin: 0 0 4px; color: #233039; font-size: 19px; text-overflow: ellipsis; white-space: nowrap; }
.states { display: flex; gap: 6px; margin-top: 8px; }
.states span { padding: 2px 6px; border-radius: 3px; font-size: 11px; }
.stage { background: #e6f0fc; color: #17619a; }
.linked { background: #e6f4e8; color: #227443; }
.unlinked { background: #f0f2f4; color: #69747b; }
.section { margin-top: 10px; background: #fff; }
.section h2 { margin: 0; padding: 14px 16px 8px; font-size: 15px; font-weight: 600; }
.section /deep/ .van-cell__value { flex: 2; overflow-wrap: anywhere; color: #36434b; }
.wecom-buttons { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; padding: 12px 16px 4px; }
.wecom-buttons .van-button { flex: 1; min-width: 0; }
.wecom-error { margin: 10px 16px 0; padding: 9px 10px; background: #fff1f0; color: #b73a35; font-size: 12px; line-height: 18px; overflow-wrap: anywhere; }
.wecom-result { margin: 10px 16px 0; padding: 9px 10px; background: #edf6ff; color: #315f80; font-size: 12px; line-height: 18px; overflow-wrap: anywhere; }
.wecom-result strong { display: block; color: #234a67; }
.wecom-result p { margin: 3px 0 0; }
.qr-wrap { display: flex; align-items: center; gap: 12px; margin: 12px 16px 0; padding: 12px; background: #f7f9fa; }
.qr-wrap img { flex: 0 0 112px; width: 112px; height: 112px; border: 1px solid #e4e7e9; background: #fff; object-fit: contain; }
.qr-preview-image { cursor: zoom-in; }
.qr-preview-image:active { opacity: .82; }
.qr-wrap p { min-width: 0; margin: 0; color: #5f6d76; font-size: 12px; line-height: 19px; overflow-wrap: anywhere; }
.sync-explain { margin: 0; padding: 10px 16px 14px; color: #7c858b; font-size: 12px; line-height: 18px; }
.remark { padding: 13px 16px; border-top: 1px solid #f0f0f0; font-size: 14px; }
.remark span { color: #646d74; }
.remark p { margin: 7px 0 0; overflow-wrap: anywhere; }
.activity-section { padding-bottom: 20px; }
.activity-sync-note { margin: -3px 16px 10px; color: #7c858b; font-size: 12px; }
.activity-day { padding: 0 16px; }
.activity-day + .activity-day { border-top: 1px solid #edf0f2; }
.activity-day-heading { display: flex; gap: 15px; padding-top: 12px; color: #333; font-size: 13px; }
.activity-timeline /deep/ .van-steps__items { padding-left: 50px; }
.activity-timeline /deep/ .van-step__title { margin-left: 5px; color: #333; }
.activity-timeline /deep/ .van-step__circle-container,
.activity-timeline /deep/ .van-step__icon--active { color: #fff; font-size: 14px; }
.activity-entry { position: relative; min-height: 62px; padding: 1px 0 12px; }
.activity-time { position: absolute; top: 1px; left: -69px; width: 42px; color: #747474; font-size: 12px; text-align: right; }
.activity-icon { position: absolute; top: 0; left: -30px; z-index: 1; display: flex; align-items: center; justify-content: center; width: 20px; height: 20px; border: 1px solid #42a4ff; border-radius: 50%; background: #fff; }
.activity-icon img { width: 10px; height: 10px; }
.activity-title { font-size: 14px; font-weight: 600; }
.activity-entry p { margin: 10px 0 0; white-space: pre-wrap; overflow-wrap: anywhere; color: #333; font-size: 14px; }
.activity-foot { margin-top: 10px; color: #747474; font-size: 12px; }
.activity-empty, .activity-error { padding: 24px 16px; color: #888; text-align: center; }
.activity-more-error { padding-top: 8px; padding-bottom: 4px; }
.activity-error button { border: 0; background: none; color: #1677c9; }
.delete-section { padding: 16px; }
.delete-error { margin: 12px 0 0; color: #c63737; font-size: 13px; line-height: 19px; overflow-wrap: anywhere; }
.more { display: block; margin: 15px auto 0; }
.action-bar { position: fixed; right: 0; bottom: 0; left: 0; display: flex; gap: 10px; max-width: 720px; margin: auto; padding: 9px 16px; border-top: 1px solid #e8ebed; background: #fff; }
.action-bar .van-button { flex: 1; }
.follow-head { display: flex; justify-content: space-between; padding: 16px; border-bottom: 1px solid #eee; }
.follow-head button { border: 0; background: none; font-size: 18px; }
.datetime-field { display: flex; align-items: center; gap: 12px; padding: 12px 16px; font-size: 14px; }
.datetime-field label { flex: 0 0 76px; }
.datetime-field input { flex: 1; min-width: 0; padding: 8px; border: 1px solid #e4e7ea; background: #fff; font: inherit; }
.follow-submit { padding: 16px; }
</style>
